import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { copyText } from "@/lib/clipboard";
import { PractitionerProfileDrawer } from "@/components/PractitionerProfileDrawer";
import { UserPlus, Mail, Trash2, Link2, Send, AlertCircle, UserX, UserCheck, CalendarClock, Pencil } from "lucide-react";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };
const ROLES = [
  { value: "owner", label: "Owner" },
  { value: "admin", label: "Admin" },
  { value: "practitioner", label: "Practitioner" },
  { value: "receptionist", label: "Receptionist" },
];
const roleColors = {
  owner: "bg-[#205B5A]/10 text-[#205B5A]",
  admin: "bg-[#496275]/10 text-[#496275]",
  practitioner: "bg-[#496275]/10 text-[#496275]",
  receptionist: "bg-amber-50 text-amber-600",
};

const errText = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d?.message) return d.message;
  return fallback;
};

export default function TeamPage() {
  const { user, loadUser } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ members: [], pending_invitations: [] });
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ email: "", role: "practitioner", full_name: "" });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [deactivating, setDeactivating] = useState(null); // { member, check }
  const [editing, setEditing] = useState(null); // member being edited

  const load = () => api.get("/team").then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const isOwner = user?.role === "owner";
  const canManagePractitioners = ["owner", "admin"].includes(user?.role);

  const openDeactivate = async (m) => {
    setError(""); setNotice(""); setBusy(m.id);
    try {
      const { data: check } = await api.get(`/practitioners/${m.id}/deactivation-check`);
      setDeactivating({ member: m, check });
    } catch (err) {
      setError(errText(err, "That practitioner could not be checked."));
    }
    setBusy("");
  };

  const confirmDeactivate = async () => {
    const m = deactivating.member;
    setBusy(m.id);
    try {
      const { data: res } = await api.post(`/practitioners/${m.id}/deactivate`);
      setNotice(res.message);
      setDeactivating(null);
      load();
    } catch (err) {
      setError(errText(err, "That practitioner could not be deactivated."));
      setDeactivating(null);
    }
    setBusy("");
  };

  const reactivate = async (m) => {
    setError(""); setNotice(""); setBusy(m.id);
    try {
      const { data: res } = await api.post(`/practitioners/${m.id}/reactivate`);
      setNotice(res.message);
      load();
    } catch (err) {
      setError(errText(err, "That practitioner could not be reactivated."));
    }
    setBusy("");
  };

  const reviewUpcoming = (m) => navigate(`/app/appointments?practitioner_id=${m.id}&needs_review=1`);

  const handleInvite = async (e) => {
    e.preventDefault();
    setError(""); setNotice(""); setBusy("invite");
    try {
      const { data: res } = await api.post("/team/invitations", form);
      setShowInvite(false);
      setForm({ email: "", role: "practitioner", full_name: "" });
      setNotice(res.message);
      load();
    } catch (err) {
      setError(errText(err, "That invitation could not be created."));
    }
    setBusy("");
  };

  const changeRole = async (m, role) => {
    setError(""); setNotice(""); setBusy(m.membership_id);
    try {
      await api.put(`/team/${m.membership_id}`, { role });
      setNotice(`${m.full_name || m.email} is now ${role === "admin" ? "an" : "a"} ${role}.`);
      load();
    } catch (err) {
      setError(errText(err, "That role could not be changed."));
    }
    setBusy("");
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.full_name || m.email} from this clinic? Their past records stay in the patient history.`)) return;
    setError(""); setNotice(""); setBusy(m.membership_id);
    try {
      await api.delete(`/team/${m.membership_id}`);
      setNotice("Team member removed.");
      load();
    } catch (err) {
      setError(errText(err, "That member could not be removed."));
    }
    setBusy("");
  };

  const resendInvite = async (inv) => {
    setError(""); setNotice(""); setBusy(inv.id);
    try {
      const { data: res } = await api.post(`/team/invitations/${inv.id}/resend`);
      setNotice(res.message);
      load();
    } catch (err) {
      setError(errText(err, "That invitation could not be resent."));
    }
    setBusy("");
  };

  const cancelInvite = async (inv) => {
    if (!window.confirm(`Cancel the invitation for ${inv.email}? The link will stop working.`)) return;
    setError(""); setNotice(""); setBusy(inv.id);
    try {
      await api.delete(`/team/invitations/${inv.id}`);
      setNotice("Invitation cancelled.");
      load();
    } catch (err) {
      setError(errText(err, "That invitation could not be cancelled."));
    }
    setBusy("");
  };

  const copyInviteLink = async (inv) => {
    const ok = await copyText(inv.invite_url);
    setNotice(ok ? "Invite link copied." : inv.invite_url);
  };

  if (loading) return <div className="p-6 md:p-8"><div className="h-8 w-40 bg-[#1A1B1A]/[0.04] rounded-xl animate-pulse" /></div>;

  return (
    <div className="p-6 md:p-8 max-w-4xl" data-testid="team-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-medium text-[#1A1B1A]" style={H}>Team</h1>
          <p className="text-sm text-[#757675]">{data.members.length} members</p>
        </div>
        {isOwner && (
          <button onClick={() => { setShowInvite(true); setError(""); }} data-testid="invite-member-btn"
            className="bg-[#205B5A] text-white px-4 py-2.5 rounded-full text-sm font-medium hover:bg-[#174544] transition-colors flex items-center gap-2">
            <UserPlus className="w-4 h-4" /> Invite member
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-xl bg-red-50 border border-red-100 px-4 py-3" data-testid="team-error">
          <AlertCircle className="w-4 h-4 text-red-500 mt-0.5" />
          <p className="text-xs text-red-600">{error}</p>
        </div>
      )}
      {notice && (
        <div className="mb-4 rounded-xl bg-[#205B5A]/[0.06] border border-[#205B5A]/10 px-4 py-3" data-testid="team-notice">
          <p className="text-xs text-[#205B5A] break-all">{notice}</p>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden mb-6">
        {data.members.map((m) => (
          <div key={m.id} className="flex items-center gap-4 px-5 py-4 border-b border-[#1A1B1A]/[0.03] last:border-0"
            data-testid={`team-member-${m.id}`}>
            <div className="w-9 h-9 rounded-full bg-[#205B5A]/10 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-semibold text-[#205B5A]">{m.full_name?.split(" ").map(n => n[0]).join("")}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#1A1B1A]">{m.full_name}{m.id === user?.id ? " (you)" : ""}</p>
              <p className="text-xs text-[#757675]">{m.email}</p>
            </div>
            {m.treats_patients && !m.is_active_practitioner && (
              <span className="text-[10px] font-medium px-2.5 py-0.5 rounded-full bg-[#1A1B1A]/[0.06] text-[#757675]"
                data-testid={`former-practitioner-${m.id}`}>Former practitioner</span>
            )}
            {isOwner ? (
              <select value={m.role} disabled={busy === m.membership_id}
                onChange={(e) => changeRole(m, e.target.value)}
                data-testid={`role-select-${m.id}`}
                className="text-xs px-2.5 py-1.5 rounded-xl border border-[#1A1B1A]/10 bg-white text-[#1A1B1A] disabled:opacity-50">
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            ) : (
              <span className={`text-[10px] font-medium px-2.5 py-0.5 rounded-full capitalize ${roleColors[m.role] || ""}`}>{m.role}</span>
            )}
            {(canManagePractitioners || m.id === user?.id) && (
              <button onClick={() => { setError(""); setNotice(""); setEditing(m); }}
                data-testid={`edit-practitioner-${m.id}`}
                className="text-[10px] px-2 py-1 rounded-lg border border-[#1A1B1A]/10 text-[#4A4B4A] hover:bg-[#FAF9F6] flex items-center gap-1">
                <Pencil className="w-3 h-3" /> Edit
              </button>
            )}
            {canManagePractitioners && m.treats_patients && (
              m.is_active_practitioner ? (
                <button onClick={() => openDeactivate(m)} disabled={busy === m.id}
                  data-testid={`deactivate-practitioner-${m.id}`}
                  className="text-[10px] px-2 py-1 rounded-lg border border-[#1A1B1A]/10 text-[#4A4B4A] hover:bg-[#FAF9F6] flex items-center gap-1 disabled:opacity-40">
                  <UserX className="w-3 h-3" /> Deactivate
                </button>
              ) : (
                <button onClick={() => reactivate(m)} disabled={busy === m.id}
                  data-testid={`reactivate-practitioner-${m.id}`}
                  className="text-[10px] px-2 py-1 rounded-lg border border-[#205B5A]/20 text-[#205B5A] hover:bg-[#205B5A]/[0.04] flex items-center gap-1 disabled:opacity-40">
                  <UserCheck className="w-3 h-3" /> Reactivate
                </button>
              )
            )}
            {isOwner && m.id !== user?.id && (
              <button onClick={() => removeMember(m)} disabled={busy === m.membership_id}
                data-testid={`remove-member-${m.id}`}
                className="p-1.5 text-[#757675] hover:text-red-500 transition-colors disabled:opacity-40">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>

      {data.pending_invitations.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-[#1A1B1A] mb-3">Pending invitations</h3>
          <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden">
            {data.pending_invitations.map((inv) => (
              <div key={inv.id} className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-[#1A1B1A]/[0.03] last:border-0"
                data-testid={`invitation-${inv.id}`}>
                <Mail className="w-4 h-4 text-[#757675]" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#4A4B4A] truncate">{inv.email}</p>
                  <p className="text-[10px] text-[#757675]">
                    {inv.is_expired ? "Link expired — resend to create a new one" : "Invitation sent, waiting to be accepted"}
                  </p>
                </div>
                <span className="text-[10px] font-medium text-[#757675] bg-[#1A1B1A]/[0.04] px-2 py-0.5 rounded-full capitalize">{inv.role}</span>
                <span className={`text-[10px] font-medium ${inv.is_expired ? "text-red-500" : "text-amber-500"}`}
                  data-testid={`invitation-status-${inv.id}`}>
                  {inv.is_expired ? "Expired" : "Pending"}
                </span>
                {isOwner && (
                  <>
                    {inv.invite_url && (
                      <button onClick={() => copyInviteLink(inv)} data-testid={`copy-invite-${inv.id}`}
                        className="text-[10px] px-2 py-1 rounded-lg border border-[#1A1B1A]/10 text-[#4A4B4A] hover:bg-[#FAF9F6] flex items-center gap-1">
                        <Link2 className="w-3 h-3" /> Copy link
                      </button>
                    )}
                    <button onClick={() => resendInvite(inv)} disabled={busy === inv.id}
                      data-testid={`resend-invite-${inv.id}`}
                      className="text-[10px] px-2 py-1 rounded-lg border border-[#1A1B1A]/10 text-[#205B5A] hover:bg-[#FAF9F6] flex items-center gap-1 disabled:opacity-40">
                      <Send className="w-3 h-3" /> Resend
                    </button>
                    <button onClick={() => cancelInvite(inv)} disabled={busy === inv.id}
                      data-testid={`cancel-invite-${inv.id}`}
                      className="text-[10px] px-2 py-1 rounded-lg text-red-600 hover:bg-red-50 flex items-center gap-1 disabled:opacity-40">
                      <Trash2 className="w-3 h-3" /> Cancel
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {editing && (
        <PractitionerProfileDrawer
          practitionerId={editing.id}
          canManage={canManagePractitioners}
          onClose={() => setEditing(null)}
          onSaved={() => { setNotice("Practitioner profile updated."); load(); loadUser(); }}
        />
      )}

      {deactivating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" data-testid="deactivate-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-medium text-[#1A1B1A] mb-2" style={H}>
              Deactivate {deactivating.member.full_name}?
            </h2>
            <p className="text-sm text-[#4A4B4A] mb-3">
              They keep their login and every past note, letter and appointment stays exactly as it is.
              They just stop appearing for new bookings.
            </p>
            {deactivating.check.upcoming_appointment_count > 0 && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 mb-3" data-testid="deactivate-upcoming-warning">
                <p className="text-xs text-amber-700 leading-relaxed">
                  This practitioner has {deactivating.check.upcoming_appointment_count} upcoming
                  appointment{deactivating.check.upcoming_appointment_count === 1 ? "" : "s"}
                  {deactivating.check.next_appointment_date ? ` from ${deactivating.check.next_appointment_date}` : ""}.
                  Deactivating them will prevent new bookings, but their existing appointments will remain
                  assigned and should be reviewed.
                </p>
                <button onClick={() => reviewUpcoming(deactivating.member)}
                  data-testid="review-upcoming-btn"
                  className="mt-2 text-[11px] font-medium text-[#205B5A] hover:underline flex items-center gap-1">
                  <CalendarClock className="w-3 h-3" /> Review upcoming appointments
                </button>
              </div>
            )}
            {deactivating.check.is_last_active_practitioner && (
              <p className="text-xs text-red-600 mb-3" data-testid="deactivate-last-warning">
                This is your only active practitioner. No new appointments can be booked until someone is active again.
              </p>
            )}
            <div className="flex justify-end gap-3 pt-1">
              <button onClick={() => setDeactivating(null)} data-testid="deactivate-cancel-btn"
                className="px-4 py-2 text-sm text-[#757675]">Cancel</button>
              <button onClick={confirmDeactivate} disabled={busy === deactivating.member.id}
                data-testid="deactivate-confirm-btn"
                className="bg-[#205B5A] text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-[#174544] disabled:opacity-50">
                Deactivate practitioner
              </button>
            </div>
          </div>
        </div>
      )}

      {showInvite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" data-testid="invite-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-medium text-[#1A1B1A] mb-4" style={H}>Invite team member</h2>
            <form onSubmit={handleInvite} className="space-y-3">
              <input value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} placeholder="Full name" data-testid="invite-name"
                className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" />
              <input value={form.email} onChange={e => setForm({...form, email: e.target.value})} type="email" required placeholder="Email address" data-testid="invite-email"
                className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" />
              <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} data-testid="invite-role"
                className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]">
                <option value="practitioner">Practitioner</option>
                <option value="receptionist">Receptionist</option>
                <option value="admin">Admin</option>
              </select>
              <p className="text-[10px] text-[#757675]">
                They'll get an email with a single-use link that expires in 7 days.
              </p>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowInvite(false)} className="px-4 py-2 text-sm text-[#757675]">Cancel</button>
                <button type="submit" disabled={busy === "invite"} data-testid="send-invite-btn"
                  className="bg-[#205B5A] text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-[#174544] disabled:opacity-50">
                  {busy === "invite" ? "Sending..." : "Send invitation"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
